"use client";

import { useAudio } from "@/app/_providers/AudioProvider";
import { Image } from "@ui/components/core/Image";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import { useTranslations } from "next-intl";
import { useState } from "react";

export enum AudioItemColor {
  BLUE = "blue",
  GREEN = "green",
  ORANGE = "orange",
  PINK = "pink",
}

const textColorVariants = cva("text-default", {
  variants: {
    color: {
      [AudioItemColor.BLUE]: "text-informative",
      [AudioItemColor.GREEN]: "text-positive",
      [AudioItemColor.ORANGE]: "text-warning",
      [AudioItemColor.PINK]: "text-pink",
    },
  },
});

export function AudioItem({
  audioUrl,
  text,
  imageUrl,
  textColor,
}: {
  audioUrl: string;
  imageUrl: string;
  text: string;
  textColor: AudioItemColor;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { playingId, play, stop } = useAudio();
  const t = useTranslations("ClientKeys.Globals");
  const isPlaying = playingId === audioUrl;

  const handlePlayPause = () => {
    if (isPlaying) {
      stop();
    } else {
      // ID is the same as audioUrl now
      play(audioUrl, audioUrl);
    }
  };

  return (
    <button
      aria-label={isPlaying ? t("pause") : t("play")}
      className='relative flex w-full items-start gap-16 rounded-xxl border border-default bg-default p-16 text-start shadow-lg transition-all duration-200'
      onClick={handlePlayPause}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='relative h-32 w-32 shrink-0 overflow-hidden rounded-full'>
        <div className={`effect-opacity absolute inset-0 ${isHovered || isPlaying ? "opacity-0" : "opacity-100"}`}>
          <Image alt='Avatar image' className='h-full w-full object-cover' height={64} src={imageUrl} width={64} />
        </div>
        <div
          className={`bg-primary effect-opacity absolute inset-0 flex items-center justify-center bg-inverse text-inverse ${isHovered || isPlaying ? "opacity-100" : "opacity-0"}`}
        >
          {isPlaying ? <Pause20 /> : <Play20 />}
        </div>
      </div>
      <p className={cn("f-ui-02 max-w-[300px] whitespace-pre-wrap", textColorVariants({ color: textColor }))}>{text}</p>
    </button>
  );
}
